import {
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  Paper,
  Text,
} from "@pillar-ui/core";
import {
  useAppStateContext,
  useCreateNewStateContext,
  useFilterStateContext,
} from "../common/contexts";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CircleCheck,
  Edit,
  GlassOff,
  Trash,
} from "@pillar-ui/icons";
import colors from "../styles/colors";
import { getRestaurantTypeIcon } from "../common/utils";
import { deleteRestaurant } from "../services";
import RatingStars from "./rating-stars";

const RestaurantEmpty = () => {
  return (
    <Paper
      as={Flex}
      flow="4"
      items="center"
      direction="col"
      width="100%"
      style={{
        marginTop: 100,
      }}
    >
      <GlassOff width="68" />
      <Heading>Không tìm thấy quán ăn nào</Heading>
      <Text width="45c" align="center" color="b" low>
        Vui lòng sửa nội dung tìm kiếm hoặc tạo quán mới
      </Text>
    </Paper>
  );
};

const RestaurantItem = (props) => {
  const { item } = props;
  const {
    setIsOpenRestaurantEditor,
    handleOpenToast,
    setIsOpenCommentEditor,
    setCommentList,
  } = useAppStateContext();
  const { setRestaurantEditorData, setCommentEditorData } =
    useCreateNewStateContext();
  const [startDelete, setStartDelete] = useState(false);
  let distanceDisplay = item?.distance ? `${item?.distance}km` : "";
  if (item?.distance < 1) {
    distanceDisplay = `${Math.round(Number(item?.distance) * 1000)}m`;
  }

  const typeIcon = useMemo(() => {
    const IconComponent = getRestaurantTypeIcon(item?.type);
    return (
      <IconComponent
        color={colors.primary}
        width={28}
        height={28}
        strokeWidth={2}
      />
    );
  }, [item?.type]);

  const handleOpenCommentDialog = useCallback(() => {
    setIsOpenCommentEditor(true);
    setCommentList(item?.comments);
    setCommentEditorData({ id: item?.id, name: item?.name });
  }, [item, setCommentList, setIsOpenCommentEditor, setCommentEditorData]);

  const ratingStars = useMemo(() => {
    const averageRating = Math.round(
      item?.comments?.reduce?.((sum, elm) => {
        return sum + Number(elm?.rating);
      }, 0) / item?.comments?.length
    );

    return (
      <RatingStars
        value={averageRating}
        onClick={handleOpenCommentDialog}
        size="md"
      />
    );
  }, [item?.comments, handleOpenCommentDialog]);

  const handleDeleteRestaurant = async () => {
    await deleteRestaurant(item?.id, (err) => {
      handleOpenToast("danger", err?.message);
    });
    handleOpenToast("success", "Quán đã được gỡ bỏ");
  };

  useEffect(() => {
    let timeoutRef = null;
    if (startDelete) {
      timeoutRef = setTimeout(() => {
        setStartDelete(false);
      }, 1000);
    }
    return () => clearTimeout(timeoutRef);
  }, [startDelete]);

  return (
    <Paper
      p="5"
      background="Se2"
      corner="5"
      style={{ position: "relative", overflow: "hidden" }}
    >
      <Paper style={{ paddingBottom: 24 }}>
        <Flex justify="between">
          <Flex items="center" gap="5">
            {typeIcon}
          </Flex>
          <Flex items="center" gap="1">
            <Paper
              onClick={() => {
                setRestaurantEditorData(item);
                setIsOpenRestaurantEditor(true);
              }}
            >
              <IconButton icon={<Edit />} size="4" color="i" />
            </Paper>
            <Paper
              onClick={() => {
                if (startDelete) {
                  handleDeleteRestaurant();
                  setStartDelete(false);
                  return;
                }
                setStartDelete(true);
              }}
            >
              <IconButton
                icon={startDelete ? <CircleCheck /> : <Trash />}
                size="4"
                color="d"
              />
            </Paper>
          </Flex>
        </Flex>
      </Paper>
      <Paper style={{ marginBottom: "8px" }}>
        <Text size="9" color="i" weight="7">
          {item?.name}
        </Text>
      </Paper>
      <Paper style={{ marginBottom: "8px" }}>{ratingStars}</Paper>
      <Paper>
        <Text size="4" color="i" weight="5">
          {`${item?.address} ${distanceDisplay ? `(~${distanceDisplay})` : ""}`}
        </Text>
      </Paper>
      <Paper>
        <Text size="4" color="i" weight="5" fontStyle="italic">
          {`Giá: ${
            item?.priceTo
              ? `${item?.priceFrom}K - ${item?.priceTo}K/người`
              : `> ${item?.priceFrom}K/người`
          }`}
        </Text>
      </Paper>
    </Paper>
  );
};

const RestaurantList = () => {
  const { restaurantList } = useAppStateContext();
  const { filterById, setFilterById } = useFilterStateContext();

  const refinedRestaurantList = useMemo(() => {
    return restaurantList?.filter?.((_item, index) => {
      return !(filterById || filterById === 0) || index === filterById;
    });
  }, [restaurantList, filterById]);

  if (!restaurantList?.length) {
    return <RestaurantEmpty />;
  }
  return (
    <Paper p="5">
      <Grid
        cols={{
          default: "1fr",
          md: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
          // xl: "repeat(3, 1fr)",
        }}
        gap="6"
      >
        {refinedRestaurantList?.map?.((item, index) => {
          return (
            <RestaurantItem key={`restaurant_item_${index + 1}`} item={item} />
          );
        })}
      </Grid>
      {/* <Flex direction="col" gap="7"></Flex> */}
      {(filterById || filterById === 0) && (
        <Paper style={{ marginTop: 24, textAlign: "center" }}>
          <Button variant="text" onClick={() => setFilterById()}>
            <Flex items="center" gap="3">
              <ArrowLeft color={colors.primary} width={28} height={28} />
              <Text size="5" color="p">
                Quay lại danh sách
              </Text>
            </Flex>
          </Button>
        </Paper>
      )}
    </Paper>
  );
};

export default RestaurantList;
